import React from 'react';
import PageLayout from '../components/PageLayout';
import { Lightbulb, Users, BarChart3, TrendingUp } from 'lucide-react';

const AboutUsPage = () => {
  return (
    <PageLayout title="About Us">
      <div className="space-y-8 text-slate-600">
        <div>
          <p className="font-semibold text-slate-900">SignalPath Digital Health Applications, LLC.</p>
          <p className="text-sm text-slate-500">Last Updated: January 01, 2026</p>
        </div>

        <section>
          <p className="mb-4">
            SignalPath develops intelligent digital health applications designed to bring clarity to the complexity of everyday health management. We believe that personal health data—when thoughtfully organized, securely managed, and intelligently interpreted—can transform how individuals understand and navigate their own journeys.
          </p>
          <p className="mb-4">
            Our focus is on structure, simplicity, and insight. By capturing life's daily inputs—habits, patterns, signals, and observations—and organizing them through intuitive digital tools, we help turn scattered information into connected understanding.
          </p>
          <p className="mb-4">
            As technology evolves, we are integrating responsible artificial intelligence to enhance pattern recognition, surface meaningful trends, and support deeper personal insight. Our approach to AI is practical and human-centered—designed to assist, not replace, individual decision-making.
          </p>
          <p>
            We are not a healthcare provider. We are a digital applications company committed to building tools that empower individuals to engage more confidently with their own data.
          </p>
        </section>

        <hr className="border-slate-300" />

        <section>
          <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-4">Our Mission</h2>
          <p>
            To develop intelligent digital applications that transform everyday health signals into meaningful insight—leveraging modern technology, including AI-driven pattern recognition, to empower individuals to better understand and navigate their personal health journeys.
          </p>
        </section>

        <hr className="border-slate-300" />

        <section>
          <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-6">Our Values</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all">
              <Lightbulb className="h-8 w-8 text-teal-500 mb-4" />
              <h3 className="font-manrope font-bold text-slate-900 mb-2">Clarity Over Complexity</h3>
              <p className="text-sm">We design tools that simplify information and make insight accessible.</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all">
              <Users className="h-8 w-8 text-teal-500 mb-4" />
              <h3 className="font-manrope font-bold text-slate-900 mb-2">Human-Centered Innovation</h3>
              <p className="text-sm">Technology—including AI—should support people, not overwhelm them.</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all">
              <BarChart3 className="h-8 w-8 text-teal-500 mb-4" />
              <h3 className="font-manrope font-bold text-slate-900 mb-2">Structured Intelligence</h3>
              <p className="text-sm">Organized data creates patterns; intelligent systems help reveal them.</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all">
              <TrendingUp className="h-8 w-8 text-teal-500 mb-4" />
              <h3 className="font-manrope font-bold text-slate-900 mb-2">Progress Through Awareness</h3>
              <p className="text-sm">Sustainable momentum begins with informed, confident decisions.</p>
            </div>
          </div>
        </section>

        <hr className="border-slate-300" />

        <section>
          <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-4">Our Story</h2>
          <p className="mb-4">
            SignalPath was founded on a simple observation: many individuals live with an abundance of health data but lack meaningful structure to interpret it. Logs, numbers, and daily inputs often exist in isolation, leaving people to make sense of complexity on their own.
          </p>
          <p className="mb-4">
            We set out to build digital tools that connect the dots—transforming scattered signals into organized insight. As advancements in artificial intelligence have accelerated, we recognized an opportunity to responsibly integrate intelligent systems that enhance pattern detection and surface personalized observations over time.
          </p>
          <p>
            What began as a focused effort to support diabetes management has evolved into a broader commitment: developing next-generation digital health applications that combine thoughtful design with intelligent technology to support real-world progress.
          </p>
        </section>

        <hr className="border-slate-300" />

        <section>
          <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-4">Our Commitment</h2>
          <p className="mb-4">
            We are committed to building responsible, secure, and human-centered digital tools. Our use of artificial intelligence is grounded in transparency and practical value—designed to assist users in recognizing patterns and trends while respecting their autonomy and privacy.
          </p>
          <p className="mb-4">
            Our applications are designed to support awareness—not replace professional care—and to provide structure without adding burden.
          </p>
          <p>
            At SignalPath, we believe that when individuals can clearly see their patterns, they gain the confidence to shape their path forward.
          </p>
        </section>
      </div>
    </PageLayout>
  );
};

export default AboutUsPage;
